import { useState, useEffect } from 'react';
import { prospectsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Trash2, RotateCcw, ShieldAlert, Search } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DeleteProspectDialog } from '@/components/prospect/DeleteProspectDialog';

const prospectLabel = (p) =>
    p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Dossier sans nom';

export function ProspectsTrashPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, prospect: null });

    // Journal d'audit
    const [auditQuery, setAuditQuery] = useState('');
    const [auditData, setAuditData] = useState({ activities: [], matched_prospects: [], query: '' });
    const [auditLoading, setAuditLoading] = useState(false);

    useEffect(() => {
        load();
        loadAudit('');
    }, []);

    const load = async () => {
        try {
            setLoading(true);
            const res = await prospectsAPI.getTrash();
            setItems(res.data || []);
        } catch (error) {
            console.error('Error loading trash:', error);
            toast.error('Erreur lors du chargement de la corbeille');
        } finally {
            setLoading(false);
        }
    };

    const loadAudit = async (q) => {
        try {
            setAuditLoading(true);
            const res = await prospectsAPI.deletionAudit(q);
            setAuditData(res.data || { activities: [], matched_prospects: [], query: '' });
        } catch (error) {
            console.error('Audit load error', error);
        } finally {
            setAuditLoading(false);
        }
    };

    const handleRestore = async (prospect) => {
        try {
            setProcessing(true);
            await prospectsAPI.restore(prospect.id);
            toast.success(`Dossier « ${prospectLabel(prospect)} » restauré`);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
        } finally {
            setProcessing(false);
        }
    };

    const handlePermanentDelete = async (confirmName) => {
        if (!deleteDialog.prospect) return;
        try {
            setProcessing(true);
            await prospectsAPI.permanentDelete(deleteDialog.prospect.id, confirmName);
            toast.success('Dossier supprimé définitivement');
            setDeleteDialog({ open: false, prospect: null });
            load();
            loadAudit(auditQuery);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression définitive');
        } finally {
            setProcessing(false);
        }
    };

    const handleRestoreAll = async () => {
        // Restauration globale : sort TOUS les dossiers de la corbeille en un seul appel.
        // Utile quand on veut vider entièrement la corbeille sans clic par clic.
        if (items.length === 0) {
            toast.info('La corbeille est déjà vide');
            return;
        }
        if (!window.confirm(`Restaurer TOUS les ${items.length} dossier(s) de la corbeille ? Ils redeviendront actifs.`)) return;
        try {
            setProcessing(true);
            const res = await prospectsAPI.restoreAll();
            toast.success(`${res.data?.restored_count ?? items.length} dossier(s) restaurés`);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration en masse');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Card data-testid="prospects-trash-panel">
            <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-amber-600" />
                        Corbeille des dossiers
                        {items.length > 0 && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800">{items.length}</Badge>
                        )}
                    </CardTitle>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-green-700 border-green-300 hover:bg-green-50"
                            onClick={handleRestoreAll}
                            disabled={loading || processing || items.length === 0}
                            data-testid="trash-restore-all-btn"
                        >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Tout restaurer{items.length > 0 ? ` (${items.length})` : ''}
                        </Button>
                        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="trash-refresh-btn">
                            Rafraîchir
                        </Button>
                    </div>
                </div>
                <p className="text-sm text-slate-500">
                    Les dossiers supprimés sont conservés ici. Vous pouvez les restaurer ou les supprimer définitivement.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-center py-8 text-slate-500">Chargement...</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">La corbeille est vide</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Dossier</TableHead>
                                <TableHead>Supprimé par</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((p) => (
                                <TableRow key={p.id} data-testid={`trash-row-${p.id}`}>
                                    <TableCell className="font-medium">{prospectLabel(p)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{p.deleted_by_name || '—'}</TableCell>
                                    <TableCell className="text-sm text-slate-500">
                                        {p.deleted_at ? format(new Date(p.deleted_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : '—'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-green-600 border-green-300 hover:bg-green-50"
                                                onClick={() => handleRestore(p)}
                                                disabled={processing}
                                                data-testid={`restore-btn-${p.id}`}
                                            >
                                                <RotateCcw className="w-4 h-4 mr-1" /> Restaurer
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 border-red-300 hover:bg-red-50"
                                                onClick={() => setDeleteDialog({ open: true, prospect: p })}
                                                disabled={processing}
                                                data-testid={`permanent-delete-btn-${p.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            <DeleteProspectDialog
                open={deleteDialog.open}
                onOpenChange={(open) => !open && setDeleteDialog({ open: false, prospect: null })}
                prospectName={deleteDialog.prospect ? prospectLabel(deleteDialog.prospect) : ''}
                processing={processing}
                onConfirm={handlePermanentDelete}
                mode="permanent"
            />

            <div className="border-t px-6 py-5 space-y-3" data-testid="deletion-audit-section">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    <div className="flex items-center gap-2 font-semibold text-slate-800">
                        <ShieldAlert className="w-4 h-4 text-amber-600" />
                        Historique des suppressions (qui / quand)
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="relative flex-1 sm:w-72">
                            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                            <Input
                                value={auditQuery}
                                onChange={(e) => setAuditQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && loadAudit(auditQuery)}
                                placeholder="Rechercher un dossier (ex : Les Rochers, Carrot)"
                                className="pl-8"
                                data-testid="deletion-audit-search"
                            />
                        </div>
                        <Button size="sm" variant="outline" onClick={() => loadAudit(auditQuery)} disabled={auditLoading} data-testid="deletion-audit-search-btn">
                            Chercher
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAuditQuery(''); loadAudit(''); }} disabled={auditLoading}>
                            Reset
                        </Button>
                    </div>
                </div>

                {auditData.query && (
                    <div className="text-xs text-slate-500">
                        Résultats pour : <strong>{auditData.query}</strong>
                        {auditData.matched_prospects?.length > 0 && (
                            <span className="ml-2 text-slate-600">
                                — {auditData.matched_prospects.length} dossier(s) trouvé(s) en base
                            </span>
                        )}
                    </div>
                )}

                {auditData.matched_prospects?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm space-y-2">
                        <div className="font-semibold text-amber-900">Dossiers correspondants (base de données)</div>
                        {auditData.matched_prospects.map((p) => (
                            <div key={p.id} className="flex flex-wrap items-center gap-2 text-slate-700">
                                <Badge variant={p.is_deleted ? 'destructive' : 'secondary'}>
                                    {p.is_deleted ? 'Dans la corbeille' : 'Actif'}
                                </Badge>
                                <span className="font-medium">{prospectLabel(p)}</span>
                                {p.deleted_by_name && (
                                    <span className="text-xs text-slate-500">
                                        supprimé par <strong>{p.deleted_by_name}</strong>
                                        {p.deleted_by_email ? ` (${p.deleted_by_email})` : ''}
                                        {p.deleted_at ? ' le ' + format(new Date(p.deleted_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : ''}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="text-xs text-slate-500 mb-1">
                    Dernières activités de suppression / restauration ({auditData.activities?.length || 0}) :
                </div>
                <div className="max-h-72 overflow-y-auto border rounded-md">
                    {auditLoading ? (
                        <div className="text-center py-4 text-slate-400 text-sm">Chargement…</div>
                    ) : (auditData.activities || []).length === 0 ? (
                        <div className="text-center py-4 text-slate-400 text-sm">Aucune activité de suppression enregistrée.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Auteur</TableHead>
                                    <TableHead>Détails</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(auditData.activities || []).map((a) => (
                                    <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                                            {a.created_at ? format(new Date(a.created_at), 'dd/MM/yy HH:mm', { locale: fr }) : '—'}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {a.action === 'prospect_hard_deleted' || a.action === 'prospect_hard_deleted_via_request' ? (
                                                <Badge variant="destructive">Définitif</Badge>
                                            ) : a.action === 'prospect_trashed' ? (
                                                <Badge className="bg-amber-100 text-amber-800">Corbeille</Badge>
                                            ) : (
                                                <Badge variant="secondary">{a.action}</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {a.author_name || '—'}
                                            {a.author_email && <div className="text-xs text-slate-500">{a.author_email}</div>}
                                        </TableCell>
                                        <TableCell className="text-xs text-slate-600 break-words">
                                            {a.details}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </Card>
    );
}

export default ProspectsTrashPanel;
